import React, { useState } from 'react';
import axios from 'axios';

export default function AdminAddProduct() {
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [form, setForm] = useState({
        name: '',
        description: '',
        price: '',
    });

    // Each variant has its own files
    const [variants, setVariants] = useState([
        { color: '', colorHex: '#000000', size: '', price: '', stock: '', files: [] }
    ]);
    const [status, setStatus] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleVariantFilesChange = (idx, e) => {
        const files = Array.from(e.target.files);
        setVariants(variants => variants.map(
            (v, i) => i === idx ? { ...v, files } : v
        ));
    };

    const handleVariantChange = (idx, e) => {
        const { name, value } = e.target;
        setVariants(variants => variants.map(
            (v, i) => i === idx ? { ...v, [name]: value } : v
        ));
    };

    const addVariant = () => {
        setVariants([
            ...variants,
            { color: '', colorHex: '#000000', size: '', price: '', stock: '', files: [] }
        ]);
    };

    const removeVariant = (idx) => {
        setVariants(variants.filter((_, i) => i !== idx));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.name) return setStatus('Enter product name');
        if (variants.length === 0) return setStatus('Add at least one variant');

        for (const [i, v] of variants.entries()) {
            if (!v.color || !v.colorHex || !v.size || !v.price || !v.stock) {
                return setStatus(`All variant fields are required (Check variant ${i + 1})`);
            }
            if (!v.files || v.files.length === 0) {
                return setStatus(`Variant ${i + 1} must have at least one image/video`);
            }
        }

        const data = new FormData();
        data.append('name', form.name);
        data.append('description', form.description);
        data.append('price', form.price);

        if (!thumbnailFile) return setStatus("Please upload a thumbnail image");
        data.append('thumbnail', thumbnailFile);

        const variantsForBackend = variants.map((v, idx) => ({
            color: v.color,
            colorHex: v.colorHex,
            size: v.size,
            price: v.price,
            stock: v.stock,
            fileKeys: v.files.map((_, fileIdx) => `variant${idx}_file${fileIdx}`)
        }));

        data.append('variants', JSON.stringify(variantsForBackend));

        variants.forEach((v, idx) => {
            v.files.forEach((file, fileIdx) => {
                data.append(`variant${idx}_file${fileIdx}`, file);
            });
        });

        try {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/products/admin`, data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setStatus('Product added!');
            setForm({ name: '', description: '', price: '' });
            setVariants([{ color: '', colorHex: '#000000', size: '', price: '', stock: '', files: [] }]);
        } catch (err) {
            console.error(err);
            setStatus('Error adding product');
        }
    };

    return (
        <form className="max-w-xl mx-auto mt-8 p-6 bg-white shadow rounded-xl" onSubmit={handleSubmit}>
            <h2 className="text-2xl font-bold mb-6">Add New Product</h2>
            <div className="mb-4">
                <label>Name:</label>
                <input name="name" value={form.name} onChange={handleChange} className="border rounded w-full p-2" required />
            </div>
            <div className="mb-4">
                <label>Description:</label>
                <textarea name="description" value={form.description} onChange={handleChange} className="border rounded w-full p-2" />
            </div>
            <div className="mb-4">
                <label>Base Price:</label>
                <input name="price" type="number" value={form.price} onChange={handleChange} className="border rounded w-full p-2" />
            </div>
            <div className="mb-4">
                <label>Variants:</label>
                {variants.map((variant, idx) => (
                    <div key={idx} className="flex flex-wrap gap-2 mb-2 items-center">
                        <input name="color" placeholder="Color" value={variant.color} onChange={e => handleVariantChange(idx, e)} className="border p-1 w-20" />
                        <input name="colorHex" type="color" value={variant.colorHex} onChange={e => handleVariantChange(idx, e)} className="w-10" />
                        <input name="size" placeholder="Size" value={variant.size} onChange={e => handleVariantChange(idx, e)} className="border p-1 w-14" />
                        <input name="price" placeholder="Price" type="number" value={variant.price} onChange={e => handleVariantChange(idx, e)} className="border p-1 w-16" />
                        <input name="stock" placeholder="Stock" type="number" value={variant.stock} onChange={e => handleVariantChange(idx, e)} className="border p-1 w-14" />
                        <input
                            type="file"
                            multiple
                            accept="image/*,video/*"
                            onChange={e => handleVariantFilesChange(idx, e)}
                            className="block"
                        />
                        <button type="button" onClick={() => removeVariant(idx)} className="text-red-600 ml-2">X</button>
                    </div>
                ))}
                <button type="button" onClick={addVariant} className="text-blue-600 text-sm mt-2">+ Add Variant</button>
            </div>
            <div className="mb-4">
                <label>Thumbnail Image:</label>
                <input
                    type="file"
                    accept="image/*"
                    onChange={e => setThumbnailFile(e.target.files[0])}
                    className="block"
                    required
                />
            </div>
            <button type="submit" className="bg-orange-500 text-white py-2 px-6 rounded hover:bg-orange-600">Add Product</button>
            <div className="mt-4 text-green-600">{status}</div>
        </form>
    );
}
